import React from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import {
  expiryEnabled,
  getExpiryDate,
  getJobAddress,
  getObjectLabels,
  getQuoteTypeOptions
} from '@paintscout/util/builder'
import addressFormat from 'address-format'
import find from 'lodash/find'
import get from 'lodash/get'
import moment from 'moment'
import { useQuote } from '../../../../context'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
          gap: theme.spacing(2)
        }
      },
      info: {
        minWidth: 0
      },
      title: {
        color: neutral.P30,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.pxToRem(28),
        lineHeight: 1.15
      },
      // Single small meta row: doc label · ID · Expires · job address, separated by comfortable gaps.
      metaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: theme.spacing(0.5, 3),
        marginTop: theme.spacing(1),
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.4
      },
      docLabel: {
        color: neutral.P40,
        fontWeight: theme.typography.fontWeightMedium
      },
      metaItem: {
        color: neutral.P50
      },
      logo: {
        flexShrink: 0,
        maxHeight: 56,
        maxWidth: 140,
        objectFit: 'contain'
      }
    }
  },
  { name: 'GatePreviewHeader' }
)

/**
 * Customer-facing header for the tiered-package gate (the estimateNext preview landing page).
 *
 * Unlike the editor's company-centric {@link QuoteDocumentHeader}, this leads with the customer's
 * name and a single compact meta row (doc label · ID · Expires · job address), with the company
 * logo pinned top-right. Read-only — this is the customer preview, so it has no edit controls.
 * Data derivations mirror QuoteDocumentHeader (quote-type company override, expiry, job address).
 */
export default function GatePreviewHeader() {
  const classes = useStyles({})
  const { quote } = useQuote()
  const { options } = useClientOptions()

  const co = options.options
  const quoteType = find(getQuoteTypeOptions({ options }), { key: quote?.int_ext?.value as any })
  const override = quoteType?.overrideCompanyOptions
  const companyName = String((override ? quoteType?.companyOptions?.name : co.companyName?.value) ?? '')
  const logo = override ? get(quoteType, 'companyOptions.logo.value', '') : co.logo?.value

  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })
  const docLabel = objectLabels.quote.value
  const quoteId = quote?.number && quote.number !== -1 ? quote.number : null

  const contact = quote?.contact
  const contactName = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') : null

  const expiryDate = expiryEnabled({ quote, options }) ? getExpiryDate({ quote }) : null
  const expires = expiryDate ? moment(expiryDate).format('MMMM D, YYYY') : null

  const jobAddress = getJobAddress({ quote })
  const jobAddressLine =
    jobAddress && (jobAddress.address || jobAddress.city || jobAddress.province || jobAddress.postal)
      ? addressFormat({
          address: jobAddress.address ?? '',
          city: jobAddress.city ?? '',
          subdivision: jobAddress.province ?? '',
          postalCode: jobAddress.postal ?? '',
          countryCode: 'US'
        }).join(', ')
      : null

  return (
    <div className={classes.root}>
      <div className={classes.info}>
        <Typography className={classes.title}>{contactName || docLabel}</Typography>
        <div className={classes.metaRow}>
          <span className={classes.docLabel}>{docLabel}</span>
          {quoteId && <span className={classes.metaItem}>ID {quoteId}</span>}
          {expires && <span className={classes.metaItem}>Expires {expires}</span>}
          {jobAddressLine && <span className={classes.metaItem}>{jobAddressLine}</span>}
        </div>
      </div>
      {logo && <img className={classes.logo} src={logo} alt={companyName || 'Company logo'} />}
    </div>
  )
}
